import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label, number } from "../../../config";
import { formatUserStatus, getAvailabilityStatus, getCustomStatus } from "../../../utils/statusFormatter";
import "./userProfileCard.scss";

/**
 * Reusable UserProfileCard component
 * @param {Object} user - User object containing name, title, avatar
 * @param {Array} fields - Array of field objects to display as tags
 * @param {Function} onActionClick - Optional action handler for top-right icon
 * @param {Object} actionIcon - Optional custom icon for action button
 * @param {String} className - Optional additional CSS classes
 * @param {Function} handleDeleteAction - Optional handler for delete/remove action
 * @param {Array} projectData - Optional array of project/team member data for validation
 * @returns {JSX.Element}
 * @author Sarthak Arora
 */
const UserProfileCard = ({
  user,
  fields = [],
  onActionClick,
  actionIcon = icon.SETTINGS,
  className = "",
  maxFields = 3,
  type,
  handleDeleteAction,
  projectData,
}) => {
  const { UserDetails, VisibleInCard } = user || {};
  const [expanded, setExpanded] = useState(false);
  const [showActionRestricted, setShowActionRestricted] = useState(false);
  const { user: currentUser } = useSelector((state) => state.auth);
  const { projectDetails } = useSelector((state) => state.projectSidebar);
  const { RoleId } = projectDetails || {};

  /*
   * Get availability status
   * Check StatusText from API, availabilityStatus, or current user's Redux state
   * @author Muskan Thakur
   */
  const availabilityStatus = useMemo(() => {
    /* Priority: StatusText (from getDirectoryUsers API) > availabilityStatus > current user's Redux state
     */
    const userForStatus =
      UserDetails?.Id === currentUser?.id || UserDetails?.id === currentUser?.id || UserDetails?.UserId === currentUser?.id
        ? currentUser
        : UserDetails;

    return getAvailabilityStatus(userForStatus);
  }, [UserDetails, currentUser]);

  const customStatus = useMemo(() => {
    const userForStatus =
      UserDetails?.Id === currentUser?.id || UserDetails?.id === currentUser?.id || UserDetails?.UserId === currentUser?.id
        ? currentUser
        : UserDetails;

    return getCustomStatus(userForStatus);
  }, [UserDetails, currentUser]);

  /* Format status text for tooltip display
   */
  const statusInfo = useMemo(() => {
    return formatUserStatus(availabilityStatus, customStatus);
  }, [availabilityStatus, customStatus]);

  useEffect(() => {
    // Initialize from global state so new mounts match current expand mode
    if (typeof window !== "undefined" && typeof window.__userProfileCardsExpanded !== "undefined") {
      setExpanded(!!window.__userProfileCardsExpanded);
    }
    const handleGlobalToggle = (e) => {
      const { expanded: isExpanded } = e?.detail || {};
      setExpanded(!!isExpanded);
    };
    window.addEventListener("userProfileCardToggle", handleGlobalToggle);
    return () => {
      window.removeEventListener("userProfileCardToggle", handleGlobalToggle);
    };
  }, []);

  const handleToggleAll = () => {
    const next = !expanded;
    if (typeof window !== "undefined") {
      window.__userProfileCardsExpanded = next;
    }
    window.dispatchEvent(new CustomEvent("userProfileCardToggle", { detail: { expanded: next } }));
  };

  const displayFields = fields.slice(0, maxFields).filter((field) => field?.name && field?.value);
  const photoLink = typeof UserDetails?.PhotoLinks === "object" ? UserDetails.PhotoLinks?.small?.toString() : UserDetails?.PhotoLinks?.toString();

  /**
   * Check if the current user is the last remaining manager (RoleId === 4)
   * @author Bhavana
   */
  const isLastManager = useMemo(() => {
    if (!projectData || !UserDetails) return false;
    const managers = projectData.filter((member) => parseInt(member.RoleId) === number.FOUR);
    return (managers?.length === number?.ONE || UserDetails?.managerCount === number?.ONE) && parseInt(UserDetails.RoleId) === number.FOUR;
  }, [projectData, UserDetails]);

  /**
   * Check if the icon should be visible
   * Icon is visible if: current user is a manager (RoleId === 4) OR it's the current user's own card
   * @author Bhavana
   */
  const shouldShowRemoveIcon = useMemo(() => {
    if (!handleDeleteAction) return false;
    const isManager = parseInt(RoleId) === number.FOUR;
    const isOwnCard = parseInt(UserDetails?.UserId) === parseInt(currentUser?.id);
    return isManager || isOwnCard;
  }, [RoleId, UserDetails?.UserId, currentUser?.id, handleDeleteAction]);

  /**
   * Handles the remove user click with validation for last manager
   * Shows restriction popup if the last manager tries to remove themselves
   * @author Bhavana
   */
  const handleRemoveClick = () => {
    if (!handleDeleteAction) return;

    if (isLastManager && parseInt(UserDetails?.UserId) === parseInt(currentUser?.id)) {
      setShowActionRestricted(true);
      return;
    }
    handleDeleteAction(UserDetails);
  };

  return (
    <div className={`user-profile-card ${className} border-profile-card p-3`}>
      <div className='user-profile-card-body p-0'>
        {onActionClick && (
          <div className='user-profile-card-header position-absolute '>
            <span className='action-icon d-flex justify-content-center align-items-center cursor-pointer' onClick={() => onActionClick(user)}>
              {actionIcon}
            </span>
          </div>
        )}

        {/* User Info Section */}
        <div className='user-info-section d-flex'>
          {statusInfo.full ? (
            <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
              <div
                className={`user-avatar mr-2 ${
                  availabilityStatus === label.AVAILABLE ? "avatar-available" : availabilityStatus === label.AWAY ? "avatar-away" : ""
                }`}
                title={statusInfo.full.replace(/[()]/g, "")}>
                {!!UserDetails?.PhotoLinks && UserDetails?.PhotoLinks ? (
                  <img src={photoLink} className={`avatar-image ${availabilityStatus === label.AWAY ? "profile-picture-away" : ""}`} />
                ) : (
                  <div className='avatar-placeholder'>
                    <span>{icon?.PERSON_GREY}</span>
                  </div>
                )}
                <span className='user-status status-online position-absolute'></span>
                <span className='user-status status-away position-absolute'></span>
              </div>
            </Tooltip>
          ) : (
            <div
              className={`user-avatar mr-2 ${
                availabilityStatus === label.AVAILABLE ? "avatar-available" : availabilityStatus === label.AWAY ? "avatar-away" : ""
              }`}>
              {!!UserDetails?.PhotoLinks && UserDetails?.PhotoLinks ? (
                <img src={photoLink} className={`avatar-image ${availabilityStatus === label.AWAY ? "profile-picture-away" : ""}`} />
              ) : (
                <div className='avatar-placeholder'>
                  <span>{icon?.PERSON_GREY}</span>
                </div>
              )}
              <span className='user-status status-online position-absolute'></span>
              <span className='user-status status-away position-absolute'></span>
            </div>
          )}
          <div className='user-details'>
            <div className='d-flex align-items-center'>
              <h6 className='user-name text-truncate mb-0' title={UserDetails?.Name || ""}>
                {UserDetails?.Name || ""}
              </h6>
              {shouldShowRemoveIcon && (
                <span className='ml-2 cursor-pointer red team-remove-user' onClick={handleRemoveClick} title={label.REMOVE_USER_HEADER}>
                  {icon.USER_MINUS}
                </span>
              )}
            </div>
            {UserDetails?.JobRole && (
              <div className='user-card__job-role text-truncate'>
                <span className='user-card__product-role small'>{UserDetails?.JobRole}</span>
              </div>
            )}
          </div>
          {type !== label.PROFILE && (
            <span className='cursor-pointer icon-small text-muted ml-2 chevron-icon' onClick={handleToggleAll}>
              {expanded ? icon.CHEVRON_UP : icon.CHEVRON_DOWN}
            </span>
          )}
        </div>
        {type !== label.PROFILE && expanded && (
          <div className={`user-profile-card ${className}`}>
            <div className='user-profile-card-body '>
              {displayFields.length > 0 && (
                <div className='d-flex user-fields-section'>
                  {displayFields.map((field, index) => {
                    const colors = field.colors;
                    return (
                      <div key={field.id || `field-${index}`} className={`user-field-tag d-flex justify-content-start py-2 border-bottom`}>
                        <span className='w-50 '>{field.name}</span>
                        <span className='pr-2 ml-1'>{field?.value === "Not selected" ? " " : "- "} </span>
                        <span className='ml-2 w-50'>{field?.value === "Not selected" ? " " : field?.value}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Action Restricted Popup for last manager */}
      {showActionRestricted && (
        <div className='action-restricted-popup shadow-sm bg-white rounded border p-2 mb-2 mt-2'>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className='font-weight-bold'>{label.ACTION_RESTRICTED}</div>
            <span className='cursor-pointer ml-2 square-icon square-icon-small' onClick={() => setShowActionRestricted(false)} title={label.CLOSE}>
              {icon.CLOSE}
            </span>
          </div>
          <div className='text-muted small'>{label.ASSIGN_NEW_MANAGER_MESSAGE}</div>
          </div>
      )}
    </div>
  );
};

export default React.memo(UserProfileCard);
